// Materiały handlowe (FrameMaterial) i ich zasoby graficzne.
// Backend zwraca kody handlowe (np. BLACK_MDF) z polem `texture_key`
// wskazującym katalog tekstur/swatchy (np. 1620_BKM_FFS).

export const DEFAULT_FRAME_MATERIAL_ID = 'BLACK_MDF';

const COMMERCIAL_ASSET_IDS = {
    BLACK_MDF: '1620_BKM_FFS',
    WHITE_MDF: '1620_WHM_FFS',
    LIGHT_WOOD_MDF: '1620_OSL_FFS',
    DARK_WOOD_MDF: '1620_OSD_FFS',
    SILVER_MDF: '1620_SLS_FFS',
    GOLD_MDF: '1620_GDG_FFS',
};

const LEGACY_ASSET_IDS = {
    '2022_UB': '1620_BKM_FFS',
    '2022_UW': '1620_WHM_FFS',
    '2022_W_OW': '1620_OSL_FFS',
    '2022_W_NB': '1620_OSD_FFS',
    '2022_G_SG': '1620_GDG_FFS',
    '2022_S_F4FB': '1620_SLS_FFS',
};

// Stare kody z frames_json → kod handlowy.
export const LEGACY_TO_COMMERCIAL = {
    CMK: 'BLACK_MDF',
    CMK_2: 'BLACK_MDF',
    '1620_BKM_FFS': 'BLACK_MDF',
    '2022_UB': 'BLACK_MDF',
    WH: 'WHITE_MDF',
    WH_2: 'WHITE_MDF',
    '1620_WHM_FFS': 'WHITE_MDF',
    '2022_UW': 'WHITE_MDF',
    GK: 'LIGHT_WOOD_MDF',
    GK_2: 'LIGHT_WOOD_MDF',
    '1620_OSL_FFS': 'LIGHT_WOOD_MDF',
    '2022_W_OW': 'LIGHT_WOOD_MDF',
    HZ: 'DARK_WOOD_MDF',
    HZ_2: 'DARK_WOOD_MDF',
    '1620_OSD_FFS': 'DARK_WOOD_MDF',
    '2022_W_NB': 'DARK_WOOD_MDF',
    DH: 'SILVER_MDF',
    DH_2: 'SILVER_MDF',
    '1620_SLS_FFS': 'SILVER_MDF',
    '2022_S_F4FB': 'SILVER_MDF',
    SG: 'GOLD_MDF',
    SG_2: 'GOLD_MDF',
    '1620_GDG_FFS': 'GOLD_MDF',
    '2022_G_SG': 'GOLD_MDF',
};

const METALLIC_ASSET_IDS = new Set(['1620_GDG_FFS', '1620_SLS_FFS']);

/** Katalog tekstur/swatchy dla materiału (obiektu z API albo samego kodu). */
export const frameMaterialAssetId = (material) => {
    const id = typeof material === 'string' ? material : material?.id;
    const textureKey = typeof material === 'object' ? material?.texture_key : null;
    return textureKey || COMMERCIAL_ASSET_IDS[id] || LEGACY_ASSET_IDS[id] || id;
};

export const isMetallicFrameMaterial = (material) => METALLIC_ASSET_IDS.has(frameMaterialAssetId(material));

/** Kod handlowy dla dowolnego (także starego) kodu materiału. */
export const canonicalFrameMaterialId = (id) => (id ? LEGACY_TO_COMMERCIAL[id] || id : id);

/**
 * Mapa materiałów (id → materiał), której get/has rozumieją aliasy.
 *
 * Lista z API i kody w danych (frames_json, frami.default_frame_material) mogą być
 * z różnych „światów” (handlowe BLACK_MDF vs stare CMK/1620_*), np. przez cache.
 * Wpisy zostają unikalne, bo values() zasila selektory materiałów.
 */
export const createFrameMaterialMap = (materials) => {
    const map = new Map((materials || []).map((item) => [item.id, item]));
    const getExact = map.get.bind(map);
    const hasExact = map.has.bind(map);

    const resolveKey = (id) => {
        if (!id) return undefined;
        if (hasExact(id)) return id;
        const canonical = canonicalFrameMaterialId(id);
        if (hasExact(canonical)) return canonical;
        for (const [key, item] of map) {
            if (canonicalFrameMaterialId(key) === canonical || item?.aliases?.includes(id)) return key;
        }
        return undefined;
    };

    map.get = (id) => {
        const key = resolveKey(id);
        return key === undefined ? undefined : getExact(key);
    };
    map.has = (id) => resolveKey(id) !== undefined;
    return map;
};
